/**
 * Field provenance (plan §6): proposed · published · confirmed · edited · unresolved.
 * Critical fields (W, H, D, stockThickness) only move to confirmed/edited by an
 * explicit act of the user; acceptNonCritical never touches them.
 */
import type { Design, FieldRecord, FieldSource, FieldStatus, Fields } from './schemas';
import { bayCount, layoutsOf } from './design';
import { CONSTRUCTION, STOCK, type ConstructionProfile } from './profiles';
import { fmtMm, fmtMm1, toMm } from './units';
import { designFromProposal } from '../families/fit';
import { familyLabel } from '../families/registry';
import { resolveStock, stockById, type AnyStock } from '../families/stock';
import { fitHistoryOf, fitHistoryState } from '../families/repair-history';
import { canonicalJson, hashOf } from './hash';

export const FIELD_STATUS: readonly FieldStatus[] = Object.freeze(['proposed', 'published', 'confirmed', 'edited', 'unresolved'] as FieldStatus[]);
/** `repairs` exists only when a fit's repair was held for review (src/families/repair-history.ts): it blocks export until accepted or edited. */
export const CRITICAL_FIELDS: readonly string[] = Object.freeze(['W', 'H', 'D', 'stockThickness', 'repairs']);
export const FIELD_LABELS: Readonly<Record<string, string>> = Object.freeze({
  family: 'Type', W: 'Width', H: 'Height', D: 'Depth', shelfCount: 'Shelves', even: 'Even openings',
  openings: 'Opening heights', openingRatios: 'Opening proportions', stockThickness: 'Plywood thickness', material: 'Material', finish: 'Finish',
  bays: 'Bays', bayWidths: 'Bay widths', back: 'Back', plinth: 'Plinth', repairs: 'Changes to the layout',
});

const nowIso = () => new Date().toISOString();

export interface FieldOpts {
  at?: string;
  by?: string;
}

/** Mark a field confirmed by the user. Returns a new fields object. */
export function confirmField(fields: Fields, key: string, { at = nowIso(), by = 'you' }: FieldOpts = {}): Fields {
  const f = fields[key];
  if (!f) return fields;
  return {
    ...fields,
    [key]: { ...f, status: 'confirmed', history: [...(f.history || []), { status: 'confirmed', value: f.value, display: f.display, at, by }] },
  };
}

/** Record a user edit. `display` is the human value, e.g. "960 mm". */
export function editField(fields: Fields, key: string, value: unknown, { display, at = nowIso(), by = 'you' }: FieldOpts & { display?: string } = {}): Fields {
  const f: FieldRecord = fields[key] ?? {
    key, label: FIELD_LABELS[key] ?? key, critical: CRITICAL_FIELDS.includes(key), value, display: '', status: 'edited', source: 'manual', history: [],
  };
  const shown = display ?? String(value);
  return {
    ...fields,
    [key]: { ...f, value, display: shown, status: 'edited', history: [...(f.history || []), { status: 'edited', value, display: shown, at, by }] },
  };
}

/** Mark a field unresolved ("I'm not sure": check with the maker). */
export function unresolveField(fields: Fields, key: string, { note = '', at = nowIso(), by = 'you' }: FieldOpts & { note?: string } = {}): Fields {
  const f = fields[key];
  if (!f) return fields;
  return {
    ...fields,
    [key]: { ...f, status: 'unresolved', note, history: [...(f.history || []), { status: 'unresolved', value: f.value, display: f.display, at, by, note }] },
  };
}

/** Confirm every non-critical field that is still proposed or published. Critical ones stay for the user. */
export function acceptNonCritical(fields: Fields, opts?: FieldOpts): Fields {
  let out = fields;
  for (const [k, f] of Object.entries(fields)) {
    if (!f.critical && (f.status === 'proposed' || f.status === 'published')) out = confirmField(out, k, opts);
  }
  return out;
}

/** Fabrication-critical fields that still need an explicit user confirmation (including unresolved ones). */
export function pendingCritical(fields: Fields): string[] {
  return CRITICAL_FIELDS.filter((k) => fields[k] && !['confirmed', 'edited'].includes(fields[k].status));
}

/** "Confirm width, height in Check first." or null when every critical field is confirmed. */
export function criticalBlocker(fields: Fields): string | null {
  const pend = pendingCritical(fields);
  if (!pend.length) return null;
  return `Confirm ${pend.map((k) => (FIELD_LABELS[k] ?? k).toLowerCase()).join(', ')} in Check first.`;
}

// ---------------------------------------------------------------------------
// Proposals and manual setup
// ---------------------------------------------------------------------------

/** The prototype's proposal-with-fields shape (the intake layer maps IntakeProposal to this). */
export interface ProposalField {
  value: unknown;
  unit?: string;
  display: string;
  status: Extract<FieldStatus, 'proposed' | 'published'>;
  source: FieldSource;
  evidence?: FieldRecord['evidence'];
}
export interface ProposalWithFields {
  createdAt: string;
  fields: Record<string, ProposalField>;
  /** The fitted family and its parameter block (FitResult), when the proposal came from a fit. */
  fit?: { familyId: string; params: unknown; shared?: { widthOverHeight: number; depthOverWidth: number } | null };
}

/** Field records produced from an analysis proposal (all 'proposed' or 'published'). */
export function fieldsFromProposal(proposal: ProposalWithFields): Fields {
  const out: Fields = {};
  for (const [k, f] of Object.entries(proposal.fields)) {
    out[k] = {
      key: k, label: FIELD_LABELS[k] ?? k, critical: CRITICAL_FIELDS.includes(k),
      value: f.value, display: f.display, status: f.status, source: f.source,
      ...(f.evidence ? { evidence: f.evidence } : {}),
      history: [{ status: f.status, value: f.value, display: f.display, at: proposal.createdAt, by: f.source === 'text' ? 'published text' : 'analysis' }],
    };
  }
  return out;
}

/**
 * A design built from a proposal (W/H/D in mm, the fitted family's block, or the first
 * slice's shelfCount, even and openingRatios bottom-up).
 */
export function proposalToDesign(proposal: ProposalWithFields, stock: AnyStock = STOCK, construction: ConstructionProfile = CONSTRUCTION): Design {
  const d = designFromProposal(proposal, stock).design;
  // other families carry their own joint profile id
  return d.params ? d : { ...d, constructionProfileId: construction.id };
}

/** Display value for a design-backed field key: [value, display]. */
export function displayFor(design: Design, key: string): [unknown, string] {
  switch (key) {
    case 'W': case 'H': case 'D': return [toMm(design[key]), fmtMm(design[key], { unit: true })];
    case 'shelfCount': return [design.n, `${design.n} shelves`];
    case 'even': return [design.even, design.even ? 'Even' : 'Uneven'];
    case 'openings': return [design.openings.map(toMm), design.openings.map((g) => fmtMm(g)).join(' / ') + ' mm'];
    case 'bays': { const n = bayCount(design); return [n, `${n} bay${n === 1 ? '' : 's'}`]; }
    case 'back': { const b = design.back ?? 'overlay'; return [b, b === 'overlay' ? 'Overlay back' : b === 'rebated' ? 'Rebated back' : 'No back']; }
    case 'plinth': return [!!design.plinth, design.plinth ? `Plinth ${fmtMm(design.plinth)} mm` : 'No plinth'];
    default: return [null, ''];
  }
}

/** Design field keys that differ between two designs (for automatic 'edited' tracking). */
export function changedFieldKeys(a: Design, b: Design): string[] {
  const keys: string[] = [];
  for (const k of ['W', 'H', 'D'] as const) if (a[k] !== b[k]) keys.push(k);
  if (a.n !== b.n) keys.push('shelfCount');
  if (a.even !== b.even) keys.push('even');
  if (a.openings.join() !== b.openings.join()) keys.push('openings');
  if (bayCount(a) !== bayCount(b)) keys.push('bays');
  if ((a.back ?? 'overlay') !== (b.back ?? 'overlay')) keys.push('back');
  if ((a.plinth ?? 0) !== (b.plinth ?? 0)) keys.push('plinth');
  if (JSON.stringify(layoutsOf(a).slice(1)) !== JSON.stringify(layoutsOf(b).slice(1)) && !keys.includes('openings')) keys.push('openings');
  return keys;
}

/**
 * Field records for "Set up by hand": every field `unresolved`, source `manual`,
 * holding the design's current values (the user confirms them in Check).
 */
export function fieldsForManualSetup(design: Design, stock: AnyStock = STOCK, { at = nowIso() }: { at?: string } = {}): Fields {
  const out: Fields = {};
  const add = (key: string, value: unknown, display: string) => {
    out[key] = {
      key, label: FIELD_LABELS[key] ?? key, critical: CRITICAL_FIELDS.includes(key), value, display,
      status: 'unresolved', source: 'manual', history: [{ status: 'unresolved', value, display, at, by: 'you' }],
    };
  };
  add('family', design.family, familyLabel(design.family));
  // other families (design.params) have no grid openings: size only; their layout is shaped in Shape
  for (const k of design.params ? ['W', 'H', 'D'] : ['W', 'H', 'D', 'shelfCount', 'even', 'openings']) {
    const [v, s] = displayFor(design, k);
    add(k, v, s);
  }
  const r = resolveStock(design.params ? stockById(design.stockProfileId) ?? stock : stock);
  add('stockThickness', toMm(r.thicknessTicks), `${fmtMm(r.nominalThicknessTicks)} mm (measured ${fmtMm1(r.thicknessTicks)})`);
  add('material', r.material, r.material);
  add('finish', r.finishSpec, r.finishSpec);
  return out;
}

// ---------------------------------------------------------------------------
// Repair holds (shelf-board-assembly plan D5)
// ---------------------------------------------------------------------------

export interface RepairState {
  /** `held`: the layout is exactly what the repair made; `changed`: the user has since changed a repaired value; `none`: nothing held. */
  state: 'none' | 'held' | 'changed';
  /** Identifies the specific repair, so an acceptance applies to it and to nothing else. */
  signature: string | null;
  reasons: string[];
}

/** The repair state is read from the design itself, so every way a design arrives (fit, Restore, Undo, a style change) agrees. */
export function repairStateOf(d: Design): RepairState {
  // a record that cannot be read fails closed: it holds the design until the user reviews it
  if (fitHistoryState(d.params).kind === 'invalid') return { state: 'held', signature: 'unreadable-record', reasons: ['the record of what the rebuild changed could not be read'] };
  const h = fitHistoryOf(d.params);
  if (!h?.hold.held || !h.repaired || typeof h.repaired !== 'object') return { state: 'none', signature: null, reasons: [] };
  const repaired = h.repaired as Record<string, unknown>;
  const params = (d.params ?? {}) as Record<string, unknown>;
  // only the values the repair produced count: values added later (fitted fronts …) do not undo a hold
  const same = Object.keys(repaired).every((k) => canonicalJson(params[k] ?? null) === canonicalJson(repaired[k] ?? null));
  return { state: same ? 'held' : 'changed', signature: hashOf({ repaired, hold: h.hold }), reasons: h.hold.reasons };
}

/** The `repairs` field for a design's repair state (null when nothing is held). */
export function repairsFieldFor(d: Design, { at = nowIso(), by = 'analysis' }: FieldOpts = {}): FieldRecord | null {
  const r = repairStateOf(d);
  if (r.state === 'none') return null;
  const held = r.state === 'held';
  const display = held ? r.reasons.join('; ') : 'Layout changed by you after the rebuild';
  const status: FieldStatus = held ? 'unresolved' : 'edited';
  return {
    key: 'repairs', label: FIELD_LABELS.repairs ?? 'Changes to the layout', critical: true, value: { signature: r.signature, reasons: r.reasons }, display, status, source: held ? 'image' : 'manual',
    ...(held ? { evidence: { note: 'To meet its construction rules the rebuild changed the layout read from the photo. Review the changes and accept them, or adjust the layout in Shape.' }, note: 'held for review' } : {}),
    history: [{ status, value: r.reasons, display, at, by: held ? by : 'you' }],
  };
}

/**
 * Keep the `repairs` field in step with the design (called on every store change): a held repair needs
 * an acceptance of that very repair; a layout the user has changed resolves it; no hold, no field.
 */
export function reconcileRepairs(fields: Fields, d: Design): Fields {
  const r = repairStateOf(d);
  const f = fields.repairs;
  if (r.state === 'none') {
    if (!f) return fields;
    const { repairs: _drop, ...rest } = fields;
    void _drop;
    return rest;
  }
  const sig = (f?.value as { signature?: string } | undefined)?.signature;
  if (f && sig === r.signature && (r.state === 'held' ? ['unresolved', 'confirmed'].includes(f.status) : f.status === 'edited')) return fields;
  return { ...fields, repairs: repairsFieldFor(d)! };
}
